import { MapClient, useMap } from "@roomservice/react";
import { useMemo } from "react";
import { ROOM_SERVICE_MAP_KEYS } from "../consts";
import { useOnlines, useSpace } from "../jotai/atoms";
import {
  AnyBlock,
  defaultFeatureFlags,
  FeatureFlags,
  SpaceInfo,
  User,
} from "../types";
import { equalPosition } from "../utils/gridPosition";
import { useMapFamily } from "./utils";

export function useBlockIds() {
  return useMapWrapper<Record<string, string>>(ROOM_SERVICE_MAP_KEYS.BLOCKIDS);
}

export function useSpaceInfo(spaceId?: string) {
  return useMapWrapper<SpaceInfo>(ROOM_SERVICE_MAP_KEYS.SPACE_INFO, spaceId);
}

// see useMapWrapper
export function useFeatureFlags() {
  const currentSpace = useSpace();
  let [map, client] = useMap<FeatureFlags>(
    currentSpace!.id,
    ROOM_SERVICE_MAP_KEYS.FLAGS
  );
  if (Object.keys(map).length === 0) {
    map = defaultFeatureFlags;
  }
  return [client ? map : undefined, client] as const;
}

export function useUserInfo(id: string | undefined) {
  let map = useMapWrapper<User>(ROOM_SERVICE_MAP_KEYS.USER + id);
  if (!id) return [null];
  return map;
}

export function useUserInfos() {
  const onlines = useOnlines();
  // hack: useMemo here to prevent maximum update depth warning
  const keys = useMemo(
    () => onlines.map((id) => ROOM_SERVICE_MAP_KEYS.USER + id),
    [onlines]
  );
  return useMapFamily<User>(keys);
}

// Convenience handler for setting/updating block MapClients
export function updateBlockMap(
  client: MapClient<AnyBlock>,
  updates: Partial<AnyBlock>,
  raise = true
) {
  // Don't bother updating position with an equal value
  if (
    updates.position &&
    equalPosition(client.get("position"), updates.position)
  ) {
    delete updates.position;
  }

  const keys = Object.keys(updates) as Array<keyof Partial<AnyBlock>>;
  keys.forEach((key: keyof AnyBlock) => {
    const blockKey: keyof AnyBlock = key;
    if (updates[key] === undefined) {
      client?.delete(blockKey);
    } else {
      client?.set(blockKey, updates[key]);
    }
  });

  // updatedAt is mostly used to power zIndex values,
  // we only want to raise block when they've been moved
  if (updates.position && raise) {
    client?.set("updatedAt", Date.now());
  }
}

// RoomService returns an empty object {} before anything has been loaded
// but its client is undefined until initial load
// this wrapper helps us tell when a map is actually empty vs still loading
// this also handles getting space ID, reducing some code
export function useMapWrapper<T>(key: string, spaceId?: string) {
  const currentSpace = useSpace();
  const [map, client] = useMap<T>(spaceId || currentSpace!.id, key);

  return [client ? map : undefined, client] as const;
}
